import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { fmtDMY, todayNZ } from "@/lib/format";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CalendarClock, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

const shift = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export function StayTaskChip({ stay, task, date, selectable = false, selected = false, onToggleSelect }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState(task.next_due);
  const today = todayNZ();
  const dueNow = task.next_due <= date;

  const move = useMutation({
    mutationFn: (to) => api.post(`/stays/${stay.id}/tasks/${task.id}/reschedule`, { date: to }),
    onSuccess: (_r, to) => {
      toast.success(t("{{task}} moved to {{date}}", { task: task.name, date: fmtDMY(to) }));
      qc.invalidateQueries({ queryKey: ["stays"] });
      qc.invalidateQueries({ queryKey: ["cleans"] });
      setOpen(false);
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const minDate = today;
  const canEarlier = shift(task.next_due, -1) >= minDate;
  const canLater = shift(task.next_due, 1) <= stay.check_out;

  if (selectable) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggleSelect?.(); }}
        aria-pressed={selected}
        data-testid={`stay-chip-${stay.room_number}-${task.id}`}
        className={`k-press rounded border px-1.5 py-0.5 font-medium ${selected ? "border-indigo-600 bg-indigo-600 text-white" : dueNow ? "border-amber-300 bg-amber-100 text-amber-800" : "border-indigo-200 bg-indigo-100 text-indigo-800"}`}
      >
        {selected ? "☑ " : "☐ "}{task.name} · {dueNow ? t("today") : fmtDMY(task.next_due).slice(0, 5)}
      </button>
    );
  }

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setPick(task.next_due < today ? today : task.next_due); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          data-testid={`stay-chip-${stay.room_number}-${task.id}`}
          title={t("Click to move to another day")}
          className={`k-press rounded px-1.5 py-0.5 font-medium hover:ring-2 hover:ring-indigo-300 ${dueNow ? "bg-amber-100 text-amber-800" : "bg-indigo-100 text-indigo-800"}`}
        >
          {task.name} · {dueNow ? t("today") : fmtDMY(task.next_due).slice(0, 5)}
          {task.next_override && <span className="ml-1 text-[10px] opacity-70">↷</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 space-y-2 p-3" onClick={(e) => e.stopPropagation()} data-testid={`stay-chip-pop-${task.id}`}>
        <div className="flex items-center gap-1.5 text-sm font-semibold"><CalendarClock className="h-4 w-4" /> {task.name}</div>
        <p className="text-xs text-muted-foreground">{t("Next due {{date}}. Move it to a day with better staffing:", { date: fmtDMY(task.next_due) })}</p>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-8 w-8" disabled={!canEarlier || move.isPending} onClick={() => move.mutate(shift(task.next_due, -1))} data-testid={`stay-chip-earlier-${task.id}`} title={t("A day earlier")}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Input type="date" value={pick} min={minDate} max={stay.check_out} onChange={(e) => setPick(e.target.value)} className="h-8 flex-1 text-xs" data-testid={`stay-chip-date-${task.id}`} />
          <Button variant="outline" size="icon" className="h-8 w-8" disabled={!canLater || move.isPending} onClick={() => move.mutate(shift(task.next_due, 1))} data-testid={`stay-chip-later-${task.id}`} title={t("A day later")}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex flex-wrap gap-1">
          <Button variant="secondary" size="sm" className="h-7 text-xs" disabled={move.isPending} onClick={() => move.mutate(today)} data-testid={`stay-chip-today-${task.id}`}>{t("Today")}</Button>
          <Button variant="secondary" size="sm" className="h-7 text-xs" disabled={move.isPending || shift(today, 1) > stay.check_out} onClick={() => move.mutate(shift(today, 1))} data-testid={`stay-chip-tomorrow-${task.id}`}>{t("Tomorrow")}</Button>
          <Button size="sm" className="ml-auto h-7 gap-1 text-xs" disabled={!pick || pick === task.next_due || move.isPending} onClick={() => move.mutate(pick)} data-testid={`stay-chip-move-${task.id}`}>
            {move.isPending && <Loader2 className="h-3 w-3 animate-spin" />} {t("Move")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
